import React from "react";
import { X, LayoutGrid } from "lucide-react";
import UserMenu from "./UserMenu";
import { appConfig } from "../../../config/app.config";
import type { SidebarBrand } from "./PcMenu";
import { Drawer } from "@packages/ui";

interface MenuItem {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface MobileMenuProps {
  visible: boolean;
  menuItems: MenuItem[];
  selectedKey: string;
  userName?: string;
  userRole?: string;
  brand?: SidebarBrand;
  onClose: () => void;
  onMenuClick: (key: string) => void;
  onProfileClick: () => void;
  onChangePasswordClick: () => void;
  onLogout: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({
  visible,
  menuItems,
  selectedKey,
  userName,
  userRole,
  brand,
  onClose,
  onMenuClick,
  onProfileClick,
  onChangePasswordClick,
  onLogout,
}) => {
  const resolvedBrand: SidebarBrand = brand ?? {
    gradient: appConfig.icon.gradient,
    icon: appConfig.icon.component,
    name: appConfig.name,
    description: appConfig.description,
  };
  const BrandIcon = resolvedBrand.icon ?? LayoutGrid;

  const handleMenuClick = (key: string) => {
    onMenuClick(key);
    onClose();
  };

  return (
    <Drawer
      isOpen={visible}
      onClose={onClose}
      side="left"
      title={`${resolvedBrand.name} navigation menu`}
      widthClassName="max-w-xs"
    >
      <div className="flex h-full flex-col bg-bg-surface shadow-xl">
            <div className="flex items-center justify-between border-b border-border-subtle p-[18px]">
              <div
                className={`flex items-center gap-3 ${resolvedBrand.onClick ? "cursor-pointer" : ""}`}
                onClick={resolvedBrand.onClick}
              >
                <div
                  className={`flex h-[38px] w-[38px] items-center justify-center rounded-[12px] bg-gradient-to-br ${resolvedBrand.gradient}`}
                >
                  <BrandIcon className="h-[19px] w-[19px] text-white" />
                </div>
                <div className="flex min-w-0 flex-col gap-[1px]">
                  <span className="truncate font-display text-[15px] font-semibold text-text-primary">
                    {resolvedBrand.name}
                  </span>
                  {resolvedBrand.description && (
                    <span className="truncate font-mono text-[10px] font-normal text-text-muted">
                      {resolvedBrand.description}
                    </span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="cursor-pointer rounded-[8px] p-2 transition-colors hover:bg-bg-elevated"
              >
                <X className="h-5 w-5 text-text-secondary" />
              </button>
            </div>

            <nav className="menu-scrollbar flex-1 overflow-y-auto px-3 py-3">
              <ul className="space-y-0.5">
                {menuItems.map((item) => {
                  const IconComponent = item.icon;
                  const active = selectedKey === item.key;
                  return (
                    <li key={item.key}>
                      <button
                        onClick={() => handleMenuClick(item.key)}
                        className={`flex w-full items-center gap-[11px] rounded-[8px] border border-transparent px-[10px] py-2 transition-colors ${
                          active
                            ? "bg-primary-soft font-semibold text-primary-ink"
                            : "text-text-primary hover:bg-bg-elevated hover:text-primary-ink"
                        }`}
                      >
                        <IconComponent className={`h-4 w-4 shrink-0 ${active ? "text-primary-ink" : "text-text-muted"}`} />
                        <span className={`min-w-0 flex-1 truncate whitespace-nowrap text-left text-sm ${active ? "font-semibold" : "font-medium"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="border-t border-border-subtle p-3">
              <UserMenu
                userName={userName}
                userRole={userRole}
                onProfileClick={() => {
                  onProfileClick();
                  onClose();
                }}
                onChangePasswordClick={() => {
                  onChangePasswordClick();
                  onClose();
                }}
                onLogout={() => {
                  onLogout();
                  onClose();
                }}
              />
            </div>
      </div>
    </Drawer>
  );
};

export default MobileMenu;
